import SettingsIcon from "@mui/icons-material/Settings";
import LockIcon from "@mui/icons-material/Lock";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import {
  AppBar,
  Button,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Toolbar,
  Tooltip,
  Box,
  Divider,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import { useGlobalSettings } from "../context/GlobalSettingsContext";
import { SimulationFormModel } from "../types/forms";
import ModeControls from "./mode-switcher/ModeControls";
import { clearSessionHistory } from "../utils/localStorage";

export default function Header({
  simulationFormModel,
  formOpen,
}: {
  simulationFormModel: SimulationFormModel;
  formOpen: boolean;
}) {
  const [settingsActive, setSettingsActive] = useState<boolean>(false);
  return (
    <AppBar position="sticky" color="primary">
      <Toolbar>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          Mortgage Simulator
        </Typography>
        {!formOpen && (
          <ModeControls simulationFormModel={simulationFormModel} />
        )}
        <IconButton
          aria-label="settings"
          size="large"
          color="inherit"
          onClick={() => setSettingsActive(true)}
        >
          <SettingsIcon fontSize="inherit" />
        </IconButton>
        <Drawer
          anchor="right"
          open={settingsActive}
          onClose={() => setSettingsActive(false)}
        >
          <GlobalSettingsForm onClose={() => setSettingsActive(false)} />
        </Drawer>
      </Toolbar>
    </AppBar>
  );
}

const currencies = [
  { value: "GBP", label: "GBP (£)" },
  { value: "EUR", label: "EUR (€)" },
  { value: "USD", label: "USD ($)" },
];

function GlobalSettingsForm({ onClose }: { onClose: () => void }) {
  const { settings, setSettings, effectiveSettings, isSettingControlled } =
    useGlobalSettings();

  const equalizeLengthControlled = isSettingControlled("equalizeLength");

  return (
    <Box
      sx={{ maxWidth: "80vw", width: 400, p: 3, bgcolor: "background.paper" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Settings
        </Typography>
        <IconButton aria-label="Close settings" size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {/* Simulation Settings Section */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Simulation Settings
      </Typography>
      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 0 }}>
        <Tooltip
          title={
            equalizeLengthControlled ? "Controlled by exploration mode" : ""
          }
          arrow
          placement="bottom"
        >
          <FormControlLabel
            control={
              <Switch
                checked={effectiveSettings.equalizeLength}
                onChange={(e) =>
                  setSettings((prev: any) => ({
                    ...prev,
                    equalizeLength: e.target.checked,
                  }))
                }
                disabled={equalizeLengthControlled}
              />
            }
            label={
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                Extend Simulations
                {equalizeLengthControlled && (
                  <LockIcon sx={{ fontSize: 16, opacity: 0.6 }} />
                )}
              </span>
            }
            disabled={equalizeLengthControlled}
            sx={{
              opacity: equalizeLengthControlled ? 0.6 : 1,
              cursor: equalizeLengthControlled ? "not-allowed" : "pointer",
              m: 0,
            }}
          />
        </Tooltip>
      </Box>
      <Typography
        variant="body2"
        color="textSecondary"
        component="div"
        sx={{ mb: 2, ml: 1 }}
      >
        Continue all simulations until the last mortgage is paid off, so
        investment interest and deposits are calculated over the same period for
        a fair comparison. This only affects investment and savings
        calculations, not mortgage interest.
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {/* Currency Section */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        General Settings
      </Typography>
      <FormControl size="small" sx={{ minWidth: 140, mb: 3 }}>
        <InputLabel id="currency-label">Currency</InputLabel>
        <Select
          labelId="currency-label"
          value={settings.currency}
          label="Currency"
          onChange={(e) => {
            setSettings((prev: any) => ({
              ...prev,
              currency: e.target.value,
            }));
          }}
        >
          {currencies.map((l) => (
            <MenuItem key={l.value} value={l.value}>
              {l.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Divider sx={{ mb: 2 }} />
      {/* Danger Zone Section */}
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, color: "error.main", mb: 1 }}
      >
        Danger Zone
      </Typography>
      <Button
        variant="outlined"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={() => {
          if (
            window.confirm(
              "Are you sure you want to clear all saved state and session history? This will refresh the page and reset the application.",
            )
          ) {
            clearSessionHistory();
            window.location.reload();
          }
        }}
        fullWidth
        sx={{ mt: 1 }}
      >
        Clear All Data
      </Button>
    </Box>
  );
}
